import { navigate } from "wouter/use-browser-location";
import { supabase, getCurrentSession } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import { integrationRagService, saveIntegrationLibraryBackup } from "@/services/integrationRagService";
import type {
  IntegrationIndexItem,
  IntegrationIndexResult,
  IntegrationSearchResult,
  IntegrationSource,
} from "@/lib/integrationRag/types";
import { documentPersistenceService, type PersistedDocument } from "@/services/documentPersistenceService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { aiAgentsService } from "@/services/aiAgentsService";
import { aiService } from "@/services/aiService";
import { operatingLoopService } from "@/services/operatingLoopService";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { useMastermind } from "@/contexts/MastermindContext";
import type { ChatThread } from "@shared/schema";
import {
  ldosContactsService,
  type ContactCreateInput,
  type LdosContact,
  type LdosContactIdentity,
  type LdosMergeCandidate,
} from "@/services/ldosContactsService";
import type { InstalledPlugin } from "../types";
import {
  registerPluginTool,
  unregisterAllPluginTools,
  unregisterPluginTool,
  type PluginTool,
  type PluginToolExecutor,
} from "../pluginToolRegistry";

/**
 * The per-plugin SDK facade — the stable API surface plugins use to reach
 * core app capabilities (RAG, documents, chat, agents, KV data, secrets,
 * records, tools, gateway fetch). Each capability namespace is gated on the
 * permission the plugin declared in its manifest, which is what the user
 * consented to at install time.
 */

const PLUGINS_BUCKET = "plugins";
const COLLECTION_NAME_REGEX = /^[a-z0-9][a-z0-9_-]{0,60}$/;

/** Mutable host state the SDK reads at call time (updated by PluginProvider). */
export interface PluginHostState {
  userId: string | null;
  workspaceId: string | null;
  spaceId: string | null;
  topicId: string | null;
}

export interface PluginSdkFetchInit {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
}

export interface PluginSdk {
  pluginId: string;
  version: string;
  manifest: InstalledPlugin["manifest"];

  /** Current workspace/space/topic/user (snapshot getters). */
  context: {
    getUserId(): string | null;
    getWorkspaceId(): string | null;
    getSpaceId(): string | null;
    getTopicId(): string | null;
  };
  /** React hooks (host singletons — safe because React is shared). */
  hooks: {
    useWorkspace: typeof useWorkspace;
    useAuth: typeof useAuth;
    useMastermind: typeof useMastermind;
  };

  rag: {
    indexItems(
      items: Array<Omit<IntegrationIndexItem, "source"> & { source?: IntegrationSource }>,
      options?: { contentMode?: "raw" | "agent" }
    ): Promise<IntegrationIndexResult>;
    search(
      query: string,
      options?: { sources?: IntegrationSource[]; limit?: number; similarityThreshold?: number }
    ): Promise<IntegrationSearchResult[]>;
    /**
     * Save (or update) a Library copy of an indexed item, keyed by
     * source + externalId so repeat saves update instead of duplicating.
     * Requires "rag" and "documents". Returns the Library item id.
     */
    saveLibraryBackup(
      input: Omit<IntegrationIndexItem, "source" | "remoteUpdatedAt"> & { source?: IntegrationSource }
    ): Promise<string>;
  };

  documents: {
    createDocument(input: {
      title: string;
      content: string;
      type?: "document" | "url";
      url?: string;
    }): Promise<PersistedDocument>;
  };

  chat: {
    createThread(input: {
      title: string;
      initialUserMessage?: string;
      linkedSpaceIds?: string[];
    }): Promise<ChatThread>;
    /** Create a seeded thread and navigate to it. Returns the thread id. */
    discussInChat(input: { title: string; text: string }): Promise<string>;
  };

  agents: {
    list: typeof aiAgentsService.getAgents;
    getById: typeof aiAgentsService.getAgentById;
    streamChat: typeof aiService.streamChat;
    /** The host's OpenRouter model catalogue (text-chat models only), so a
     *  plugin's model picker can offer every model the ai-proxy routes to
     *  instead of a hard-coded handful. Read-only; the plugin still passes
     *  the chosen slug per request through streamChat (R-041). */
    models: typeof aiService.fetchModels;
  };

  data: {
    get<T = unknown>(key: string): Promise<T | null>;
    set(key: string, value: unknown): Promise<void>;
    delete(key: string): Promise<void>;
  };

  secrets: {
    /** Store a secret (used server-side by the gateway via {{secret:KEY}}). */
    set(key: string, value: string): Promise<void>;
    exists(key: string): Promise<boolean>;
    delete(key: string): Promise<void>;
  };

  tools: {
    register(tool: PluginTool, executor: PluginToolExecutor): string;
    unregister(toolId: string): void;
    unregisterAll(): void;
  };

  records: {
    upsert(collection: string, key: string, data: unknown): Promise<void>;
    get<T = unknown>(collection: string, key: string): Promise<T | null>;
    insert(collection: string, data: unknown): Promise<string>;
    list<T = unknown>(
      collection: string,
      opts?: { limit?: number; offset?: number; ascending?: boolean }
    ): Promise<Array<{ id: string; key: string | null; data: T; createdAt: string; updatedAt: string }>>;
    remove(collection: string, key: string): Promise<void>;
    clear(collection: string): Promise<void>;
  };

  /**
   * LDOS Contacts — the city-center CRM utility (work order S11d). Every
   * method is scoped to the CALLING PLUGIN's app_slug via
   * ldos_contact_app_links and to the user via RLS: a plugin can only see
   * contacts its app has been granted. Per-app visibility is opt-in, never
   * global — a pastoral contact in Prayer LIFE must not appear in a business
   * pipeline. create() runs the S11e match ladder before insert; update()
   * pins edited fields as source='manual'; remove() tombstones and unlinks
   * and NEVER propagates the delete outward.
   */
  contacts: {
    list(params?: {
      q?: string;
      favourite?: boolean;
      source?: string;
      limit?: number;
      cursor?: string;
    }): Promise<{ contacts: LdosContact[]; nextCursor: string | null }>;
    get(id: string): Promise<LdosContact>;
    create(input: ContactCreateInput): Promise<LdosContact>;
    update(id: string, patch: Partial<ContactCreateInput>): Promise<LdosContact>;
    remove(id: string): Promise<void>;
    setFavourite(id: string, value: boolean): Promise<void>;
    merge(primaryId: string, duplicateId: string): Promise<LdosContact>;
    listMergeCandidates(): Promise<LdosMergeCandidate[]>;
    markNotDuplicate(contactId: string, candidateId: string): Promise<void>;
    identities(id: string): Promise<LdosContactIdentity[]>;
    importVCard(file: string | Blob): Promise<{ created: number; merged: number; skipped: number }>;
    exportVCard(ids?: string[]): Promise<Blob>;
  };

  /**
   * Requires "core-api". Call host integration endpoints with the signed-in
   * user's JWT. In production, `/api/clickup` (and other inbox proxies) are
   * rewritten to the matching Supabase Edge Function.
   */
  coreApi: {
    fetch(path: string, init?: PluginSdkFetchInit): Promise<Response>;
    functionsBase(): string;
  };

  /**
   * Authenticated fetch through the plugin gateway. The target host must be
   * declared as "network:<host>" in the manifest. Header values may contain
   * "{{secret:KEY}}" placeholders substituted server-side.
   */
  fetch(url: string, init?: PluginSdkFetchInit): Promise<Response>;

  toast: typeof toast;
  navigate(path: string): void;
  queryClient: typeof queryClient;
  /** Public URL of a file inside this plugin's bundle (e.g. "assets/logo.png"). */
  assetUrl(relativePath: string): string;
  /** Escape hatch: the host Supabase client (RLS-scoped to the current user). */
  supabase: typeof supabase;
}

function gatewayBaseUrl(): string {
  if (import.meta.env.DEV) return "/api/plugin-proxy";
  return `${functionsBaseUrl()}/plugin-proxy`;
}

function functionsBaseUrl(): string {
  const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/$/, "");
  return `${supabaseUrl}/functions/v1`;
}

/** Platforms whose data APIs live on Edge Functions in production (same as host services). */
const EDGE_PROXY_BY_API: Record<string, string> = {
  clickup: "clickup-proxy",
  fireflies: "fireflies-proxy",
  gohighlevel: "gohighlevel-proxy",
  ringcentral: "ringcentral-proxy",
  zoom: "zoom-proxy",
};

/**
 * Resolve a core-api path. DEV keeps same-origin `/api/*` (Express).
 * Production rewrites inbox data routes to `{functionsBase}/{platform}-proxy`.
 * OAuth and `/sync` stay on Vercel.
 */
function resolveCoreApiUrl(path: string): string {
  const functionsBase = functionsBaseUrl();
  if (path === functionsBase || path.startsWith(`${functionsBase}/`)) {
    return path;
  }
  if (!path.startsWith("/api/")) {
    throw new Error('sdk.coreApi.fetch path must start with "/api/" or functionsBase()');
  }
  if (import.meta.env.DEV) {
    return path;
  }

  const q = path.indexOf("?");
  const pathname = q === -1 ? path : path.slice(0, q);
  const search = q === -1 ? "" : path.slice(q);
  const rest = pathname.slice("/api/".length);
  const slash = rest.indexOf("/");
  const platform = slash === -1 ? rest : rest.slice(0, slash);
  const remainder = slash === -1 ? "" : rest.slice(slash);
  if (remainder.startsWith("/oauth") || remainder.startsWith("/sync")) {
    return path;
  }
  const proxy = EDGE_PROXY_BY_API[platform];
  if (!proxy) {
    return path;
  }
  return `${functionsBase}/${proxy}${remainder}${search}`;
}

async function requireUserId(): Promise<string> {
  const session = await getCurrentSession();
  const userId = session?.user?.id;
  if (!userId) throw new Error("Not signed in");
  return userId;
}

function assertCollectionName(collection: string): void {
  if (!COLLECTION_NAME_REGEX.test(collection)) {
    throw new Error(`Invalid records collection name "${collection}"`);
  }
}

type PluginRecordRow = {
  id: string;
  key: string | null;
  data: unknown;
  created_at: string;
  updated_at: string;
};

function mapRecordRow<T>(row: PluginRecordRow): {
  id: string;
  key: string | null;
  data: T;
  createdAt: string;
  updatedAt: string;
} {
  return {
    id: row.id,
    key: row.key,
    data: row.data as T,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function createPluginSdk(plugin: InstalledPlugin, hostState: PluginHostState): PluginSdk {
  const pluginId = plugin.plugin_id;
  const permissions: string[] = (plugin.manifest.permissions ?? []) as string[];

  const requirePermission = (perm: string): void => {
    if (!permissions.includes(perm)) {
      throw new Error(`Plugin "${pluginId}" does not have the "${perm}" permission`);
    }
  };

  const defaultSource = pluginId as IntegrationSource;

  const createThread = async (input: {
    title: string;
    initialUserMessage?: string;
    linkedSpaceIds?: string[];
  }): Promise<ChatThread> => {
    requirePermission("chat");
    const messages = input.initialUserMessage
      ? [
          {
            id: crypto.randomUUID(),
            role: "user",
            content: input.initialUserMessage,
            timestamp: new Date().toISOString(),
          },
        ]
      : [];
    return chatThreadsService.createChatThread(
      {
        title: input.title,
        messages,
        linkedSpaceIds: input.linkedSpaceIds ?? (hostState.spaceId ? [hostState.spaceId] : []),
      } as Parameters<typeof chatThreadsService.createChatThread>[0],
      hostState.workspaceId
    );
  };

  return {
    pluginId,
    version: plugin.version,
    manifest: plugin.manifest,

    context: {
      getUserId: () => hostState.userId,
      getWorkspaceId: () => hostState.workspaceId,
      getSpaceId: () => hostState.spaceId,
      getTopicId: () => hostState.topicId,
    },
    hooks: { useWorkspace, useAuth, useMastermind },

    rag: {
      async indexItems(items, options) {
        requirePermission("rag");
        const withSource: IntegrationIndexItem[] = items.map((item) => ({
          ...item,
          source: item.source ?? defaultSource,
        }));
        return integrationRagService.indexItems(withSource, options);
      },
      async search(query, options) {
        requirePermission("rag");
        return integrationRagService.search(query, {
          sources: options?.sources ?? [defaultSource],
          limit: options?.limit,
          similarityThreshold: options?.similarityThreshold,
        });
      },
      async saveLibraryBackup(input) {
        requirePermission("rag");
        requirePermission("documents");
        return saveIntegrationLibraryBackup({
          source: input.source ?? defaultSource,
          externalId: input.externalId,
          title: input.title,
          content: input.content,
          metadata: input.metadata,
          workspaceId: hostState.workspaceId,
        });
      },
    },

    documents: {
      async createDocument(input) {
        requirePermission("documents");
        return documentPersistenceService.persistDocument({
          title: input.title,
          content: input.content,
          type: input.type ?? "document",
          url: input.url,
          context: {
            workspaceId: hostState.workspaceId,
            spaceId: hostState.spaceId,
            topicId: hostState.topicId,
          },
        });
      },
    },

    chat: {
      createThread,
      async discussInChat(input) {
        const thread = await createThread({
          title: input.title,
          initialUserMessage: input.text,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
        queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries"], exact: false });
        navigate(`/chat?threadId=${encodeURIComponent(thread.id)}`);
        return thread.id;
      },
    },

    agents: {
      list: () => {
        requirePermission("agents");
        return aiAgentsService.getAgents();
      },
      getById: (id: string) => {
        requirePermission("agents");
        return aiAgentsService.getAgentById(id);
      },
      streamChat: ((options: Parameters<typeof aiService.streamChat>[0]) => {
        requirePermission("agents");
        return operatingLoopService.streamGuarded({ ...options, app: options.app ?? "agents" });
      }) as typeof aiService.streamChat,
      models: (() => {
        requirePermission("agents");
        return aiService.fetchModels();
      }) as typeof aiService.fetchModels,
    },

    data: {
      async get<T>(key: string): Promise<T | null> {
        requirePermission("storage-kv");
        const userId = await requireUserId();
        const { data, error } = await supabase
          .from("plugin_data")
          .select("value")
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("key", key)
          .maybeSingle();
        if (error) throw new Error(error.message);
        return (data?.value ?? null) as T | null;
      },
      async set(key: string, value: unknown): Promise<void> {
        requirePermission("storage-kv");
        const userId = await requireUserId();
        const { error } = await supabase.from("plugin_data").upsert(
          {
            user_id: userId,
            plugin_id: pluginId,
            key,
            value,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,plugin_id,key" }
        );
        if (error) throw new Error(error.message);
      },
      async delete(key: string): Promise<void> {
        requirePermission("storage-kv");
        const userId = await requireUserId();
        const { error } = await supabase
          .from("plugin_data")
          .delete()
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("key", key);
        if (error) throw new Error(error.message);
      },
    },

    secrets: {
      async set(key: string, value: string): Promise<void> {
        requirePermission("secrets");
        const userId = await requireUserId();
        const { error } = await supabase.from("plugin_secrets").upsert(
          {
            user_id: userId,
            plugin_id: pluginId,
            key,
            value,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,plugin_id,key" }
        );
        if (error) throw new Error(error.message);
      },
      async exists(key: string): Promise<boolean> {
        requirePermission("secrets");
        const userId = await requireUserId();
        const { data, error } = await supabase
          .from("plugin_secrets")
          .select("key")
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("key", key)
          .maybeSingle();
        if (error) throw new Error(error.message);
        return !!data;
      },
      async delete(key: string): Promise<void> {
        requirePermission("secrets");
        const userId = await requireUserId();
        const { error } = await supabase
          .from("plugin_secrets")
          .delete()
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("key", key);
        if (error) throw new Error(error.message);
      },
    },

    tools: {
      register(tool, executor) {
        requirePermission("tools");
        return registerPluginTool(pluginId, plugin.name, tool, executor);
      },
      unregister(toolId) {
        requirePermission("tools");
        unregisterPluginTool(pluginId, toolId);
      },
      unregisterAll() {
        requirePermission("tools");
        unregisterAllPluginTools(pluginId);
      },
    },

    records: {
      async upsert(collection, key, data) {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const now = new Date().toISOString();
        const { error } = await supabase.from("plugin_records").upsert(
          {
            user_id: userId,
            plugin_id: pluginId,
            collection,
            key,
            data,
            updated_at: now,
          },
          { onConflict: "user_id,plugin_id,collection,key" }
        );
        if (error) throw new Error(error.message);
      },
      async get<T>(collection: string, key: string): Promise<T | null> {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const { data, error } = await supabase
          .from("plugin_records")
          .select("data")
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("collection", collection)
          .eq("key", key)
          .maybeSingle();
        if (error) throw new Error(error.message);
        return (data?.data ?? null) as T | null;
      },
      async insert(collection, data): Promise<string> {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const now = new Date().toISOString();
        const { data: row, error } = await supabase
          .from("plugin_records")
          .insert({
            user_id: userId,
            plugin_id: pluginId,
            collection,
            key: null,
            data,
            created_at: now,
            updated_at: now,
          })
          .select("id")
          .single();
        if (error) throw new Error(error.message);
        return row.id as string;
      },
      async list<T>(
        collection: string,
        opts?: { limit?: number; offset?: number; ascending?: boolean }
      ) {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const limit = Math.min(Math.max(opts?.limit ?? 100, 1), 500);
        const offset = Math.max(opts?.offset ?? 0, 0);
        const ascending = opts?.ascending ?? false;
        const { data, error } = await supabase
          .from("plugin_records")
          .select("id, key, data, created_at, updated_at")
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("collection", collection)
          .order("updated_at", { ascending })
          .range(offset, offset + limit - 1);
        if (error) throw new Error(error.message);
        return (data ?? []).map((row: PluginRecordRow) => mapRecordRow<T>(row));
      },
      async remove(collection, key) {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const { error } = await supabase
          .from("plugin_records")
          .delete()
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("collection", collection)
          .eq("key", key);
        if (error) throw new Error(error.message);
      },
      async clear(collection) {
        requirePermission("records");
        assertCollectionName(collection);
        const userId = await requireUserId();
        const { error } = await supabase
          .from("plugin_records")
          .delete()
          .eq("user_id", userId)
          .eq("plugin_id", pluginId)
          .eq("collection", collection);
        if (error) throw new Error(error.message);
      },
    },

    // S11d: the namespace that makes the CRM a city-center utility. The
    // calling plugin's OWN id is the app_slug — a plugin cannot name another
    // app's slug, so it can never widen its own visibility grant.
    contacts: {
      async list(params) {
        requirePermission("contacts");
        return ldosContactsService.list(pluginId, params);
      },
      async get(id) {
        requirePermission("contacts");
        return ldosContactsService.get(pluginId, id);
      },
      async create(input) {
        requirePermission("contacts");
        return ldosContactsService.create(pluginId, input);
      },
      async update(id, patch) {
        requirePermission("contacts");
        return ldosContactsService.update(pluginId, id, patch);
      },
      async remove(id) {
        requirePermission("contacts");
        return ldosContactsService.remove(pluginId, id);
      },
      async setFavourite(id, value) {
        requirePermission("contacts");
        return ldosContactsService.setFavourite(pluginId, id, value);
      },
      async merge(primaryId, duplicateId) {
        requirePermission("contacts");
        return ldosContactsService.merge(pluginId, primaryId, duplicateId);
      },
      async listMergeCandidates() {
        requirePermission("contacts");
        return ldosContactsService.listMergeCandidates();
      },
      async markNotDuplicate(contactId, candidateId) {
        requirePermission("contacts");
        return ldosContactsService.markNotDuplicate(contactId, candidateId);
      },
      async identities(id) {
        requirePermission("contacts");
        return ldosContactsService.identities(pluginId, id);
      },
      async importVCard(file) {
        requirePermission("contacts");
        return ldosContactsService.importVCard(pluginId, file);
      },
      async exportVCard(ids) {
        requirePermission("contacts");
        return ldosContactsService.exportVCard(pluginId, ids);
      },
    },

    coreApi: {
      functionsBase() {
        requirePermission("core-api");
        return functionsBaseUrl();
      },
      async fetch(path: string, init?: PluginSdkFetchInit): Promise<Response> {
        requirePermission("core-api");
        const session = await getCurrentSession();
        if (!session?.access_token) throw new Error("Not signed in");
        const url = resolveCoreApiUrl(path);
        const method = (init?.method ?? "GET").toUpperCase();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          ...(init?.headers ?? {}),
          Authorization: `Bearer ${session.access_token}`,
        };
        const body =
          init?.body == null || method === "GET" || method === "HEAD"
            ? undefined
            : typeof init.body === "string"
              ? init.body
              : JSON.stringify(init.body);
        return fetch(url, { method, headers, body });
      },
    },

    async fetch(url: string, init?: PluginSdkFetchInit): Promise<Response> {
      const session = await getCurrentSession();
      if (!session?.access_token) throw new Error("Not signed in");
      return fetch(gatewayBaseUrl(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pluginId,
          url,
          method: init?.method ?? "GET",
          headers: init?.headers ?? {},
          body: init?.body,
        }),
      });
    },

    toast,
    navigate: (path: string) => navigate(path),
    queryClient,
    assetUrl(relativePath: string): string {
      const clean = relativePath.replace(/^\/+/, "");
      const { data } = supabase.storage
        .from(PLUGINS_BUCKET)
        .getPublicUrl(`${plugin.storage_path}/${clean}`);
      return data.publicUrl;
    },
    supabase,
  };
}
